import store from '@/store';
import { signOutGontijoSession } from '@/actions/gontijoSession';
import { PaymentPreconditionError } from '@/services/purchase/paymentPreconditionError';
import {
  selectGontijoClientAuth,
  selectGontijoPaymentBlocked,
} from 'selectors/gontijoSessionSelectors';

/**
 * The purchase calls that must carry the buyer's Gontijo session, with the token attached for them.
 *
 * The token is a **body field**, not a header. platform-purchase reads it as a ticket attribute and
 * out of the payment's `meta`, then builds `GTJ-CLI-Authorization` itself on the way to the ERP. A
 * header interceptor — the usual answer to "propagate this everywhere" — would attach it to nothing
 * the backend reads.
 *
 * Decorating the payload at the client call (`services/purchase/createPayment`) rather than in the
 * payload builders is what keeps it out of call sites. There are a dozen of those and one of this,
 * and the alternative was worse than verbose: the engine payload spreads *after*
 * `getPaymentPayload` at every assembly site, so a `meta` put in the builder is silently overwritten
 * by any engine that sets its own — which Cielo does, for 3D Secure, and Cielo is Gontijo's engine.
 * That failure would have shown up only on authenticated 3DS payments in production, which is the
 * worst place to find it.
 */

/**
 * The session token for this request, or null when none should travel.
 *
 * Never throws. `store.getState()` raises if the store has not been created — which cannot happen
 * on the way to a payment in the running app, but is the normal state of a unit test exercising one.
 * More to the point, "we could not read the session" must never be the reason a payment fails: the
 * worst acceptable outcome is a request that goes out exactly as it did before this change.
 */
function currentClientAuth() {
  try {
    return selectGontijoClientAuth(store.getState());
  } catch {
    return null;
  }
}

/**
 * The payload with `meta.client_auth` merged in, or untouched when there is no session.
 *
 * Merged into whatever `meta` is already there — Cielo puts the 3D Secure fields in that same
 * object — rather than replacing it.
 * @param {Object} payload - The payment payload.
 * @returns {Object} The payload the backend should receive.
 */
export function withClientAuth(payload) {
  const clientAuth = currentClientAuth();
  if (!clientAuth) return payload;

  return { ...payload, meta: { ...(payload?.meta || {}), client_auth: clientAuth } };
}

/**
 * The same question as `selectGontijoPaymentBlocked`, asked against the store right now.
 *
 * `useSelector` re-runs when the store changes, and nothing dispatches when a clock passes an
 * expiry. So a buyer who arrived signed in, left the checkout open past `expiresAt` and then
 * pressed pay kept a rendered `false` all the way through the guard — while `withClientAuth`, which
 * does read the current time, answered null and sent the payment with no token. Read at the moment
 * of payment, the two agree.
 *
 * Lives here, beside `withClientAuth`, rather than with the selectors: reading the store directly
 * is the point — the rendered value is exactly what cannot be trusted — and selectors take state.
 * @returns {boolean} True when payment must not be launched.
 */
export function isGontijoPaymentBlockedNow() {
  try {
    return selectGontijoPaymentBlocked(store.getState());
  } catch {
    // Before the store exists there is no session either, so nothing to hold.
    return false;
  }
}

/**
 * The `code` of the refusal {@link gontijoSessionPrecondition} returns when the session is required
 * and gone.
 */
export const GONTIJO_SESSION_REQUIRED = 'gontijo_session_required';

/**
 * Refuses the payment, before any request, when the tenant requires the session and there is no
 * valid one. Listed in `services/purchase/createPayment`'s preconditions.
 *
 * The checkout guards hold the pay button, but they run before the engine builds its payload, and
 * Cielo runs 3D Secure inside that step: a session that expires during the challenge reaches
 * `createPayment` with no token, and the backend would refuse it anyway. The refusal carries its
 * own message and sign-out, so the shared failure handler needs no Gontijo branch. A store that
 * cannot be read still lets the payment through, as `withClientAuth` does.
 *
 * Never inside the native app's WebView, which `selectGontijoPaymentBlocked` owns: the exemption
 * reaches here through {@link isGontijoPaymentBlockedNow}, and holds until the app hands its token
 * to the WebView.
 * @returns {?PaymentPreconditionError} The refusal, or null to let the payment through.
 */
export function gontijoSessionPrecondition() {
  if (!isGontijoPaymentBlockedNow()) return null;

  return new PaymentPreconditionError({
    code: GONTIJO_SESSION_REQUIRED,
    notificationKey: 'gontijo_session_expired',
    // Signing the expired session out re-renders the checkout gate with its sign-in, which a stale
    // rendered session would otherwise keep hidden.
    followUp: signOutGontijoSession,
    message: 'Gontijo payment requires a valid client session',
  });
}
